import { useState } from 'react'
import { Copy, Lock } from 'lucide-react'
import type { TeamScopeKey } from '../../types/database'
import { scopeLabel } from '../../lib/teamScopes'
import { useAuth } from '../../contexts/AuthContext'

type Props = { scope: TeamScopeKey }

export default function NotAccessibleCard({ scope }: Props) {
  const { adminDisplayName, adminEmail } = useAuth()
  const [copied, setCopied] = useState(false)

  async function copyEmail() {
    if (!adminEmail) return
    try {
      await navigator.clipboard.writeText(adminEmail)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard denied or insecure context — silent fail.
    }
  }

  const adminName = adminDisplayName ?? 'your admin'

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-gray-800/40 border border-white/[0.06] rounded-2xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/50 text-gray-400 mb-4">
          <Lock size={20} />
        </div>
        <h2 className="text-base font-semibold text-gray-100 mb-2">
          Not Accessible
        </h2>
        <p className="text-sm text-gray-400 mb-5">
          You don&rsquo;t have access to{' '}
          <span className="text-gray-200 font-medium">{scopeLabel(scope)}</span>.
          Ask {adminName} to enable this permission from their Team settings.
        </p>
        {adminEmail && (
          <button
            type="button"
            onClick={copyEmail}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-700/50 text-gray-200 text-xs font-medium hover:bg-gray-700 transition-colors"
          >
            <Copy size={12} />
            {copied ? 'Copied!' : `Copy ${adminEmail}`}
          </button>
        )}
      </div>
    </div>
  )
}
